import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, Clock, Building2, CheckCircle2, ArrowRight, Target, Lightbulb, TrendingUp } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { getReferenceById, getRelatedReferences } from '@/data/references';
import { services } from '@/data/services';
import { IMAGE_WIDTHS, buildSrc, buildSrcSet } from '@/lib/responsiveImage';
import { generalContact, decodeContactInfo } from '@/data/contacts';

const contactEmail = decodeContactInfo(generalContact.email);
const contactPhone = decodeContactInfo(generalContact.phone);

const ReferenceDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const reference = getReferenceById(id || '');
  const heroImageBase = reference?.screenshotDesktop || reference?.image;
  const heroImage = heroImageBase ? buildSrc(heroImageBase, 1366) : undefined;
  const heroImageSrcSet = heroImageBase ? buildSrcSet(heroImageBase, IMAGE_WIDTHS.reference) : undefined;
  const heroImageSizes = '(min-width: 1024px) 50vw, 100vw';
  const serviceLinkMap = [
    { match: /seo/i, slug: 'seo-optimierung' },
    { match: /sitemap/i, slug: 'seo-optimierung' },
    { match: /buchung|booking/i, slug: 'buchungssysteme' },
    { match: /webdesign|webseite|react|performance/i, slug: 'webseitenerstellung' },
    { match: /content|newsletter/i, slug: 'webseitenerstellung' },
    { match: /hosting|betrieb|resend|turnstile|formular/i, slug: 'hosting-domains' },
    { match: /karriere/i, slug: 'karriereseiten' },
    { match: /digital|beratung/i, slug: 'digitalisierung-beratung' },
  ];
  
  if (!reference) {
    return <Navigate to="/referenzen" replace />;
  }

  const relatedReferences = getRelatedReferences(reference.id, reference.industry);
  const relatedServices = Array.from(
    new Map(
      serviceLinkMap
        .filter(({ match }) => reference.services.some((service) => match.test(service)))
        .map(({ slug }) => services.find((service) => service.slug === slug))
        .filter(Boolean)
        .map((service) => [service!.slug, service]),
    ).values(),
  );

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main id="main-content" tabIndex={-1}>
      
      {/* Hero Section - Dark */}
      <section className="pt-24 pb-16 relative overflow-hidden section-dark">
        <div className="container mx-auto px-6 relative">
          {/* Back Link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-8"
          >
            <Link 
              to="/referenzen" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Zurück zu allen Referenzen
            </Link>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${reference.color} text-white`}>
                  {reference.category}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white">
                  {reference.industry}
                </span>
              </div>
              
              <h1 className="text-3xl md:text-5xl font-display font-bold mb-6 leading-tight text-white">
                {reference.headline}
              </h1>
              
              <p className="text-lg text-white/80 mb-8 leading-relaxed">
                {reference.summary}
              </p>

              <Button asChild size="lg" className="group">
                <a href={reference.url} target="_blank" rel="noopener noreferrer">
                  {reference.urlLabel || 'Live-Projekt ansehen'}
                  <ExternalLink className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </a>
              </Button>

            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <img
                  src={heroImage}
                  srcSet={heroImageSrcSet}
                  sizes={heroImageSizes}
                  alt={reference.title}
                  width={1366}
                  height={768}
                  decoding="async"
                  className="w-full aspect-video object-cover object-top"
                />
              </div>
              {/* Decorative elements */}
              <div className={`absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br ${reference.color} rounded-2xl opacity-30 -z-10`} />
              <div className={`absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br ${reference.color} rounded-xl opacity-30 -z-10`} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Facts - Light */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-6"
          >
            <div className="bg-card rounded-xl p-6 border border-border shadow-lg">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-3">
                <Building2 className="w-5 h-5 text-blue-500" aria-hidden="true" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">Kunde</p>
              <p className="font-semibold text-card-foreground">{reference.client}</p>
            </div>
            <div className="bg-card rounded-xl p-6 border border-border shadow-lg">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-3">
                <Clock className="w-5 h-5 text-amber-500" aria-hidden="true" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">Projektdauer</p>
              <p className="font-semibold text-card-foreground">{reference.duration}</p>
            </div>
            <div className="bg-card rounded-xl p-6 border border-border md:col-span-2 shadow-lg">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center mb-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" aria-hidden="true" />
              </div>
              <p className="text-sm text-muted-foreground mb-2">Unsere Leistungen</p>
              <div className="flex flex-wrap gap-2">
                {reference.services.map((service) => (
                  <span key={service} className="px-3 py-1 bg-muted text-foreground rounded-full text-sm">
                    {service}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Challenge Section - Light */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-primary font-medium text-sm uppercase tracking-wider">Die Herausforderung</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold mt-4 mb-6 text-foreground">
                Was war das Problem?
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {reference.challenge}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-card rounded-2xl p-8 border border-rose-200 shadow-lg">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-rose-500/20 to-red-500/20 flex items-center justify-center mb-4">
                  <Target className="w-7 h-7 text-rose-500" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-card-foreground">Ausgangssituation</h3>
                <ul className="space-y-2 text-muted-foreground">
                  {reference.challengePoints.map((point) => (
                    <li key={point} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-2 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Solution Section - Dark */}
      <section className="py-20 section-dark">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative order-2 lg:order-1"
            >
              <div className="bg-card rounded-2xl p-8 border border-emerald-200 shadow-lg">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center mb-4">
                  <Lightbulb className="w-7 h-7 text-emerald-500" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-card-foreground">Unser Ansatz</h3>
                <ul className="space-y-2 text-muted-foreground">
                  {reference.approachPoints.map((point) => (
                    <li key={point} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="text-primary font-medium text-sm uppercase tracking-wider">Die Lösung</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold mt-4 mb-6 text-white">
                Was war unser Einsatz?
              </h2>
              <p className="text-white/80 text-lg leading-relaxed">
                {reference.solution}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Results Section - Light */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-violet-500" aria-hidden="true" />
              </div>
            </div>
            <span className="text-primary font-medium text-sm uppercase tracking-wider">Das Ergebnis</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold mt-4 mb-6 text-foreground">
                <span className="gradient-text">Was waren die messbaren Erfolge?</span>
              </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {reference.result}
            </p>
          </motion.div>

          {reference.resultStats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-6 mb-12"
            >
              {reference.resultStats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card rounded-2xl p-8 border border-border text-center hover:border-primary/50 transition-colors shadow-lg"
                >
                  <div className={`text-3xl md:text-4xl lg:text-5xl font-display font-bold bg-gradient-to-r ${reference.color} bg-clip-text text-transparent mb-2 break-words hyphens-auto`} lang="de">
                    {stat.value}
                  </div>
                  <p className="text-muted-foreground">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {relatedServices.length > 0 && (
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">
                Welche Leistungen passen zu diesem Projekt?
              </h2>
              <p className="text-muted-foreground">
                Entdecken Sie, welche Leistungen hinter diesem Ergebnis stehen.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {relatedServices.map((service) => (
                <Link
                  key={service!.slug}
                  to={`/leistungen/${service!.slug}`}
                  className="bg-card rounded-2xl p-6 border border-border hover:border-primary/50 transition-colors shadow-lg"
                >
                  <h3 className="text-lg font-display font-semibold text-card-foreground mb-2">
                    {service!.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {service!.description}
                  </p>
                  <span className="text-primary text-sm font-medium inline-flex items-center gap-1 mt-4">
                    Leistung ansehen
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - Dark */}
      <section className="py-20 section-dark">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center bg-card rounded-3xl p-12 border border-border shadow-2xl"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 text-card-foreground">
              Haben Sie eine ähnliche Herausforderung?
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Lassen Sie uns gemeinsam Ihr Projekt zum Erfolg führen. 
              Wir freuen uns auf Ihre Anfrage!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="group">
                <Link to="/kontakt">
                  Projekt anfragen
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-foreground border-border hover:bg-muted">
                <a href={reference.url} target="_blank" rel="noopener noreferrer">
                  Live-Projekt ansehen
                  <ExternalLink className="w-4 h-4 ml-2" aria-hidden="true" />
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Projects - Light */}
      {relatedReferences.length > 0 && (
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground">
                Das könnte Sie auch interessieren
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {relatedReferences.map((ref, index) => (
                <motion.div
                  key={ref.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/referenzen/${ref.id}`}
                    className="group block bg-card rounded-2xl overflow-hidden border border-border hover:border-primary/50 transition-all shadow-lg hover:shadow-xl"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={buildSrc(ref.image, 1024)}
                        srcSet={buildSrcSet(ref.image, IMAGE_WIDTHS.reference)}
                        sizes="(min-width: 768px) 50vw, 100vw"
                        alt={ref.title}
                        loading="lazy"
                        decoding="async"
                        width={1024}
                        height={897}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-display font-semibold mb-2 text-card-foreground group-hover:text-primary transition-colors">
                        {ref.title}
                      </h3>
                      <p className="text-muted-foreground text-sm line-clamp-2">
                        {ref.summary}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      </main>
      <Footer />
    </div>
  );
};

export default ReferenceDetailPage;
